import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Clock, CheckCircle, XCircle, Wallet, TrendingUp } from 'lucide-react';
import { getCurrentUser, User } from '../lib/auth';
import IdentityVerification from '../components/IdentityVerification';

interface CreditBalance {
  basicCredits: number;
  enhancedCredits: number;
  totalCredits: number;
}

interface Verification {
  id: string;
  idType: string;
  verificationType?: 'basic' | 'enhanced';
  status: 'verified' | 'failed' | 'pending';
  createdAt: string;
  confidenceScore?: number;
}

export default function Dashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showVerificationFlow, setShowVerificationFlow] = useState(false);
  const [isAssisted, setIsAssisted] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Handle post-payment redirection
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setShowVerificationFlow(true);
      // NOTE: We no longer clear the URL here immediately. 
      // IdentityVerification needs to read these params to resume state.
      // IdentityVerification will clear them once it consumes them.
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load user
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      // Load credits
      const creditsResponse = await fetch('/api/credits/balance', {
        credentials: 'include',
      });
      if (creditsResponse.ok) {
        const creditsData = await creditsResponse.json();
        if (creditsData.success) {
          setCredits(creditsData.data);
        }
      }

      // Load recent verifications
      const verificationsResponse = await fetch('/api/verifications?limit=5', {
        credentials: 'include',
      });
      if (verificationsResponse.ok) {
        const verificationsData = await verificationsResponse.json();
        // The API returns { data: { verifications: [], pagination: {} } }
        if (verificationsData.success && verificationsData.data) {
          const list = Array.isArray(verificationsData.data) 
            ? verificationsData.data 
            : (verificationsData.data.verifications || []);
          setVerifications(list);
        } else {
          setVerifications([]);
        }
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const formatIdType = (idType: string) => {
    const types: Record<string, string> = {
      philid: 'PhilID',
      umid: 'UMID',
      drivers_license: "Driver's License",
      passport: 'Passport',
      postal_id: 'Postal ID',
      tin: 'TIN ID',
      philhealth: 'PhilHealth ID',
      voters_id: "Voter's ID",
    };
    return types[idType] || idType.toUpperCase();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (showVerificationFlow) {
    return (
      <div className="min-h-screen bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setShowVerificationFlow(false)}
            className="mb-6 text-blue-400 hover:text-blue-300 transition-colors"
          >
            ← Back to Dashboard
          </button>
          <IdentityVerification isAssisted={isAssisted} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome back, {user?.fullName || user?.email?.split('@')[0] || 'User'}! 👋
          </h1>
          <p className="text-gray-400">
            Manage your verifications and credits from your dashboard
          </p>
        </motion.div>

        {/* Credit Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Wallet className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Credit Balance</h2>
                <p className="text-sm text-gray-400">Available verification credits</p>
              </div>
            </div>
            <Link
              to="/pricing"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm font-medium"
            >
              Buy Credits
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-emerald-400" />
                <p className="text-sm text-gray-400">Basic Credits</p>
              </div>
              <p className="text-2xl font-bold text-white">{credits?.basicCredits || 0}</p>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <p className="text-sm text-gray-400">Enhanced Credits</p>
              </div>
              <p className="text-2xl font-bold text-white">{credits?.enhancedCredits || 0}</p>
            </div>

            <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg p-4 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-4 h-4 text-blue-400" />
                <p className="text-sm text-gray-300 font-medium">Total Available</p>
              </div>
              <p className="text-2xl font-bold text-white">{credits?.totalCredits || 0}</p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <button
              onClick={() => setShowVerificationFlow(true)}
              className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg font-semibold text-lg transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
            >
              Start New Verification
            </button>

            {/* Assisted Mode Toggle */}
            <div className="flex items-center gap-3 px-6 py-4 glass-card bg-slate-800/40 border-slate-700/50 w-full md:w-auto">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-white">Assisted Mode</span>
                <span className="text-xs text-gray-400">Skip liveness for in-person customers</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer"
                  checked={isAssisted}
                  onChange={(e) => setIsAssisted(e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </motion.div>

        {/* Recent Verifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6"
        >
          <h2 className="text-xl font-bold text-white mb-4">Recent Verifications</h2>

          {verifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-gray-500" />
              </div>
              <p className="text-gray-400 mb-2">No verifications yet</p>
              <p className="text-sm text-gray-500">
                Start your first verification to see it here
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700/50">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">ID Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Level</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Confidence</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {verifications.map((verification) => (
                    <tr
                      key={verification.id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(verification.status)}
                          <span className="text-sm text-gray-300 capitalize">
                            {verification.status}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-300">
                        {formatIdType(verification.idType)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                          verification.verificationType === 'enhanced' 
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                            : 'bg-gray-700/50 text-gray-400 border border-gray-600/30'
                        }`}>
                          {verification.verificationType || 'basic'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {verification.confidenceScore ? (
                          <span className="text-sm text-gray-300">
                            {(verification.confidenceScore * 100).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-400">
                        {new Date(verification.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <Link
                          to={`/verify/${verification.id}`}
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}